import { Link } from "react-router-dom";
import {
  articles,
  categories,
  featuredArticleSlugs,
  testimonials,
  siteInfo,
} from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/base/ArticleCard";
import { useState } from "react";

const featuredArticles = featuredArticleSlugs
  .map((slug) => articles.find((a) => a.slug === slug))
  .filter(Boolean) as typeof articles;

export default function Home() {
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubscribeStatus("success");
    setEmail("");
    setTimeout(() => setSubscribeStatus("idle"), 3000);
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    description: siteInfo.description,
    url: "https://meinvgaoji.com",
    logo: "https://meinvgaoji.com/logo.png",
    contactPoint: {
      "@type": "ContactPoint",
      email: siteInfo.email,
      contactType: "customer service",
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteInfo.name,
    description: siteInfo.description,
    url: "https://meinvgaoji.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://meinvgaoji.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "什么是真正的闺蜜关系？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "真正的闺蜜关系是建立在相互信任、真诚和共同成长基础上的深厚女性友谊。她不仅是生活中的陪伴者，更是情感上的支持者和成长中的见证者。",
        },
      },
      {
        "@type": "Question",
        name: "如何维持长久的闺蜜情谊？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "维持长久的闺蜜情谊需要保持定期联系但不过度依赖、学会倾听而非急于给建议、在人生重要时刻彼此陪伴、尊重各自的边界，以及持续创造新的美好回忆。",
        },
      },
      {
        "@type": "Question",
        name: "结婚后如何维系闺蜜关系？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "结婚后可以通过建立固定的见面制度、争取伴侣的理解与支持、将闺蜜融入家庭生活等方式来维系友谊。好的婚姻应该容纳你的人际关系，而不是取代它们。",
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-16">
          <div className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Group%20of%20diverse%20Chinese%20young%20women%20walking%20together%20in%20a%20sunlit%20urban%20park%2C%20laughing%20and%20sharing%20stories%2C%20genuine%20friendship%20and%20connection%2C%20golden%20hour%20warm%20light%2C%20cream%20and%20blush%20soft%20tones%2C%20artistic%20lifestyle%20photography&width=1600&height=800&seq=hero-home-banner&orientation=landscape"
              alt={`${siteInfo.name} - ${siteInfo.tagline}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          </div>
          <div className="absolute inset-0 pt-16 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 max-w-3xl leading-tight">
              {siteInfo.tagline}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link
                to="/category/jiemei-qinggan"
                className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                探索故事
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 px-6 py-3 rounded-full font-medium text-sm hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Overview */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                探索闺蜜世界
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                从相处之道到情感故事，发现女生之间最真挚的友谊
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cat.description}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm text-primary-500 font-medium">
                    <span>浏览文章</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  精选文章
                </h2>
                <p className="text-sm text-foreground-600">
                  用心筛选的闺蜜故事与情感指南
                </p>
              </div>
              <Link
                to="/category/gurien-xiangchu"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles Grid */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新发布
                </h2>
                <p className="text-sm text-foreground-600">
                  持续更新，分享更多闺蜜故事
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {articles.slice(0, 8).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              姐妹们怎么说
            </h2>
            <p className="text-foreground-600 mb-10">
              来自读者的真实分享，每一段故事都温暖有力
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-left"
                >
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="text-xs text-foreground-500">
                    <span className="font-medium text-foreground-800">
                      {t.name}
                    </span>
                    ，{t.age}岁，{t.city}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-14 md:py-20">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
            <div className="bg-accent-50 border border-accent-100/70 rounded-xl p-8 md:p-12">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
                <i className="ri-mail-send-line text-2xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                订阅闺蜜故事
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                每周一封温暖邮件，分享最新的闺蜜故事与情感指南
              </p>
              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入你的邮箱地址"
                  className="flex-1 px-4 py-3 rounded-full border border-background-300/60 bg-white text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
                  required
                />
                <button
                  type="submit"
                  className="bg-accent-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  订阅
                </button>
              </form>
              {subscribeStatus === "success" && (
                <p className="mt-3 text-sm text-accent-600 font-medium">
                  订阅成功！欢迎加入我们的闺蜜大家庭~
                </p>
              )}
              <p className="mt-4 text-xs text-foreground-400">
                我们尊重你的隐私，不会发送垃圾邮件
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}